import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Layers, Settings, BarChart3, LogOut, Users,
  Activity, Zap, Box, Plus, Trash2, RefreshCw, AlertCircle, Mail,
  Menu, Loader2, Home, Code, FileText, Image, Edit3,
  Save, X, ChevronDown, Filter, Download, Upload
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

// Import existing APIs
import {
  heroApi, aboutApi, serviceApi, projectApi, contactApi,
  pagesApi, statsApi, themeApi, sectionApi, authApi, aboutPageContentApi
} from '../../services/api';

// Import existing sections
import ModernHeroEditor from './Sections/ModernHeroEditor';
import AboutEditor from './Sections/AboutEditor';
import ModernServicesEditor from './Sections/ModernServicesEditor';
import ModernPortfolioEditor from './Sections/ModernPortfolioEditor';
import ContactManager from './Sections/ContactManager';
import ThemeSettingsEditor from './Sections/ThemeEditor';
import HeaderSettingsContent from './Sections/SiteHeaderEditor';
import FooterEditor from './Sections/FooterEditor';
import PreloaderSettings from './Sections/PreloaderSettings';
import SettingsContent from './Sections/SettingsContent';
import WorksSectionEditor from './Sections/WorksSectionEditor';
import type { AboutSlide } from './types';

// Types
interface DashboardStats {
  totalProjects: number;
  totalServices: number;
  totalContacts: number;
  totalPages: number;
  recentActivity: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: string;
  }>;
}

interface ModernDashboardProps {
  onBackToSite: () => void;
}

const ModernDashboard: React.FC<ModernDashboardProps> = ({ onBackToSite }) => {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();
  const activeTab = section || 'overview';
  
  const setActiveTab = (tab: string) => {
    if (tab === 'overview') {
      navigate('/dashboard');
    } else {
      navigate(`/dashboard/${tab}`);
    }
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);

  // Data states
  const [stats, setStats] = useState<any>(null);
  const [heroSections, setHeroSections] = useState<any>([]);
  const [services, setServices] = useState<any>([]);
  const [projects, setProjects] = useState<any>([]);
  const [pages, setPages] = useState<any>([]);
  const [contactSubmissions, setContactSubmissions] = useState<any>([]);
  const [aboutSlides, setAboutSlides] = useState<AboutSlide[]>([]);
  const [editingAboutId, setEditingAboutId] = useState<number | null>(null);
  const [editingAboutData, setEditingAboutData] = useState<Partial<AboutSlide>>({});
  const [pendingAboutImageFile, setPendingAboutImageFile] = useState<File | null>(null);
  const [pendingAboutImagePreview, setPendingAboutImagePreview] = useState<string | null>(null);
  const [modelSource, setModelSource] = useState<'default' | 'custom'>('default');
  const [showInactive] = useState(true);
  const [aboutHeading, setAboutHeading] = useState<{ heading: string; description: string; cta_heading: string; cta_description: string; button_text: string; button_action: 'modal' | 'link'; button_link: string }>({
    heading: 'About Us',
    description: '',
    cta_heading: 'Let\'s Build Something Great',
    cta_description: 'Every detail on this page is connected with Dashboard About section.',
    button_text: 'Start a Project',
    button_action: 'modal',
    button_link: '',
  });
  const aboutHistoryRef = useRef<{ heading: string; description: string; at: string }[]>([]);

  const handleUpdateContactStatus = async (id: number, status: 'new'|'read'|'replied'|'archived') => {
    try {
      const response = await contactApi.updateStatus(id, status);
      if (response?.success) {
        setContactSubmissions((prev: any[]) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
      }
    } catch (err) {
      console.error('Failed to update contact status', err);
    }
  };

  const handleDeleteContact = async (id: number) => {
    if (!window.confirm('Delete this contact submission?')) return;
    try {
      const response = await contactApi.delete(id);
      if (response?.success) {
        setContactSubmissions((prev: any[]) => prev.filter((s) => s.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete contact submission', err);
    }
  };

  const handleBulkDeleteContact = async (ids: number[]) => {
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected submissions?`)) return;
    try {
      const response = await contactApi.deleteMany(ids);
      if (response?.success) {
        setContactSubmissions((prev: any[]) => prev.filter((s) => !ids.includes(s.id)));
      }
    } catch (err) {
      console.error('Failed to bulk delete contact submissions', err);
    }
  };

  // Load initial data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Use fallback data if API fails
      const fallbackStats = {
        totalProjects: 6,
        totalServices: 4,
        totalContacts: 12,
        totalPages: 3,
        recentActivity: []
      };

      const fallbackHero = [
        {
          id: '1',
          heading: 'Exhibitions',
          description: 'Immersive environments that captivate and engage.',
          image: 'https://images.unsplash.com/photo-1526498460520-4c246339dccb?auto=format&fit=crop&w=900&q=80',
          isActive: true,
          displayOrder: 0
        }
      ];

      const fallbackServices = [
        {
          id: '1',
          title: 'Immersive Exhibitions',
          description: 'Large-scale interactive environments for museums, galleries, and brand spaces.',
          image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1600&q=80',
          slug: 'immersive-exhibitions',
          isActive: true,
          displayOrder: 0
        }
      ];

      try {
        const [statsRes, heroRes, servicesRes, projectsRes, contactsRes, pagesRes, aboutRes] = await Promise.all([
          statsApi.getDashboardStats ? statsApi.getDashboardStats() : Promise.resolve({ data: fallbackStats }),
          heroApi.getAll ? heroApi.getAll() : Promise.resolve({ data: fallbackHero }),
          serviceApi.getAll ? serviceApi.getAll() : Promise.resolve({ data: fallbackServices }),
          projectApi.getAll ? projectApi.getAll() : Promise.resolve({ data: [] }),
          contactApi.getAll ? contactApi.getAll() : Promise.resolve({ data: [] }),
          pagesApi.getAll ? pagesApi.getAll() : Promise.resolve({ data: [] }),
          aboutApi.getAll ? aboutApi.getAll(true) : Promise.resolve({ data: [] })
        ]);

        setStats(statsRes?.data || fallbackStats);
        setHeroSections(heroRes?.data || fallbackHero);
        setServices(servicesRes?.data || fallbackServices);
        setProjects(projectsRes?.data || []);
        setContactSubmissions(contactsRes?.data || []);
        setPages(pagesRes?.data || []);
        setAboutSlides(Array.isArray(aboutRes?.data) ? aboutRes.data : []);
        try {
          const [aboutHeadingRes, aboutPageContentRes] = await Promise.all([
            sectionApi.get('about'),
            aboutPageContentApi.get(),
          ]);
          if (aboutHeadingRes?.success && aboutHeadingRes?.data) {
            setAboutHeading({
              heading: String(aboutHeadingRes.data.heading || 'About Us'),
              description: String(aboutHeadingRes.data.description || ''),
              cta_heading: String(aboutPageContentRes?.data?.cta_heading || 'Let\'s Build Something Great'),
              cta_description: String(aboutPageContentRes?.data?.cta_description || 'Every detail on this page is connected with Dashboard About section.'),
              button_text: String(aboutPageContentRes?.data?.button_text || 'Start a Project'),
              button_action: aboutPageContentRes?.data?.button_action === 'link' ? 'link' : 'modal',
              button_link: String(aboutPageContentRes?.data?.button_link || ''),
            });
          }
        } catch { void 0; }
        try {
          const settingsRes = await aboutApi.getSettings();
          if (settingsRes?.success && settingsRes.data && editingAboutId == null) {
            setModelSource(settingsRes.data.model_source === 'custom' ? 'custom' : 'default');
            setEditingAboutData((prev) => ({
              ...prev,
              component_type: settingsRes.data.component_type || '',
              model_url: settingsRes.data.model_url || '',
              animation_settings: settingsRes.data.animation_settings || {},
            }));
          }
        } catch { void 0; }
      } catch (apiError) {
        console.warn('API calls failed, using fallback data:', apiError);
        setStats(fallbackStats);
        setHeroSections(fallbackHero);
        setServices(fallbackServices);
        setProjects([]);
        setContactSubmissions([]);
        setPages([]);
        setAboutSlides([]);
      }
    } catch (err) {
      console.error('Dashboard data loading error:', err);
      setError('Failed to load dashboard data');
      // Set fallback data even on error
      setStats({
        totalProjects: 0,
        totalServices: 0,
        totalContacts: 0,
        totalPages: 0,
        recentActivity: []
      });
      setHeroSections([]);
      setServices([]);
      setProjects([]);
      setContactSubmissions([]);
      setPages([]);
      setAboutSlides([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSectionHeadingSave = async () => {
    try {
      setError('');
      const sectionPayload = {
        heading: aboutHeading.heading,
        description: aboutHeading.description,
      };
      const ctaPayload = {
        cta_heading: aboutHeading.cta_heading,
        cta_description: aboutHeading.cta_description,
        button_text: aboutHeading.button_text,
        button_action: aboutHeading.button_action,
        button_link: aboutHeading.button_action === 'link' ? aboutHeading.button_link : null,
      };
      const [sectionRes, ctaRes] = await Promise.all([
        sectionApi.update('about', sectionPayload),
        aboutPageContentApi.update(ctaPayload),
      ]);
      if (sectionRes?.success && ctaRes?.success) {
        aboutHistoryRef.current = [{ heading: aboutHeading.heading, description: aboutHeading.description, at: new Date().toISOString() }, ...aboutHistoryRef.current].slice(0, 8);
        window.dispatchEvent(new Event('about:updated'));
        setSuccess('About heading updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update about heading';
      setError(msg);
    }
  };

  const handleEditAbout = (slide: AboutSlide) => {
    if (pendingAboutImagePreview) URL.revokeObjectURL(pendingAboutImagePreview);
    setPendingAboutImageFile(null);
    setPendingAboutImagePreview(null);
    setEditingAboutId(slide.id);
    setEditingAboutData({ ...slide });
    setModelSource(slide.model_url ? 'custom' : 'default');
  };

  const handleCreateAbout = () => {
    if (pendingAboutImagePreview) URL.revokeObjectURL(pendingAboutImagePreview);
    setPendingAboutImageFile(null);
    setPendingAboutImagePreview(null);
    setEditingAboutId(-1);
    setModelSource('default');
    setEditingAboutData({
      slide_id: String(Date.now()),
      title: '',
      subtitle: '',
      description: '',
      theme: 'cyan',
      icon_name: 'Box',
      component_type: 'cybercube',
      display_order: aboutSlides.length + 1,
      is_active: true,
      features: [],
    });
  };

  const buildAboutPayload = (id?: number) => {
    const current = id && id > 0 ? aboutSlides.find((s) => s.id === id) : null;
    const rawImage = String((editingAboutData as any).image_url ?? (current as any)?.image_url ?? (current as any)?.image ?? '');
    const safeImage = rawImage.startsWith('blob:') ? '' : rawImage;
    return {
      slide_id: String((editingAboutData as any).slide_id ?? (current as any)?.slide_id ?? Date.now()),
      title: String(editingAboutData.title ?? current?.title ?? ''),
      description: String(editingAboutData.description ?? current?.description ?? ''),
      theme: String((editingAboutData as any).theme ?? (current as any)?.theme ?? 'cyan'),
      subtitle: String((editingAboutData as any).subtitle ?? (current as any)?.subtitle ?? ''),
      icon_name: String((editingAboutData as any).icon_name ?? (current as any)?.icon_name ?? 'Box'),
      component_type: String((editingAboutData as any).component_type ?? (current as any)?.component_type ?? 'cybercube'),
      display_order: Number((editingAboutData as any).display_order ?? (current as any)?.display_order ?? Math.max(aboutSlides.length, 0) + 1),
      image: safeImage,
      is_active: Boolean(editingAboutData.is_active ?? current?.is_active ?? true),
      gradient_start: String((editingAboutData as any).gradient_start ?? (current as any)?.gradient_start ?? '#000000'),
      gradient_end: String((editingAboutData as any).gradient_end ?? (current as any)?.gradient_end ?? '#000000'),
      features: Array.isArray((editingAboutData as any).features)
        ? (editingAboutData as any).features
        : Array.isArray((current as any)?.features)
          ? (current as any).features
          : []
    };
  };

  const handleSaveAbout = async (id: number) => {
    try {
      setError('');
      const payload = buildAboutPayload(id);
      if (id === -1) {
        const response = await aboutApi.create(payload);
        if (response?.success) {
          const savedId = Number(response?.data?.id);
          if (pendingAboutImageFile && savedId > 0) {
            const formData = new FormData();
            formData.append('image', pendingAboutImageFile);
            await aboutApi.uploadImage(savedId, formData);
          }
          if (pendingAboutImagePreview) URL.revokeObjectURL(pendingAboutImagePreview);
          setPendingAboutImageFile(null);
          setPendingAboutImagePreview(null);
          setEditingAboutId(null);
          setEditingAboutData({});
          window.dispatchEvent(new Event('about:updated'));
          setSuccess('About item created successfully!');
          setTimeout(() => setSuccess(''), 3000);
          await loadDashboardData();
        }
        return;
      }
      const response = await aboutApi.update(id, payload);
      if (response?.success) {
        if (pendingAboutImageFile) {
          const formData = new FormData();
          formData.append('image', pendingAboutImageFile);
          await aboutApi.uploadImage(id, formData);
        }
        if (pendingAboutImagePreview) URL.revokeObjectURL(pendingAboutImagePreview);
        setPendingAboutImageFile(null);
        setPendingAboutImagePreview(null);
        setEditingAboutId(null);
        setEditingAboutData({});
        window.dispatchEvent(new Event('about:updated'));
        setSuccess('About item updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
        await loadDashboardData();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save about item';
      setError(msg);
    }
  };

  const handleToggleAboutActive = async (id: number, currentStatus: boolean) => {
    try {
      setError('');
      const slide = aboutSlides.find((s) => s.id === id);
      if (!slide) return;
      const payload = {
        slide_id: slide.slide_id,
        title: slide.title,
        subtitle: slide.subtitle,
        description: slide.description,
        theme: slide.theme,
        icon_name: slide.icon_name,
        component_type: slide.component_type,
        display_order: slide.display_order,
        image_url: slide.image_url,
        is_active: !currentStatus,
      };
      let response = await aboutApi.update(id, payload);
      if (!response?.success) response = await aboutApi.update(id, { is_active: !currentStatus });
      if (response?.success) {
        window.dispatchEvent(new Event('about:updated'));
        setSuccess('About status updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
        await loadDashboardData();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update about status';
      setError(msg);
    }
  };

  const handleDeleteAbout = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this about item?')) return;
    try {
      setError('');
      const response = await aboutApi.delete(id);
      if (response?.success) {
        window.dispatchEvent(new Event('about:updated'));
        setSuccess('About item deleted');
        setTimeout(() => setSuccess(''), 3000);
        await loadDashboardData();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete about item';
      setError(msg);
    }
  };

  const handleUploadAboutImage = async (id?: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;
      try {
        if (pendingAboutImagePreview) URL.revokeObjectURL(pendingAboutImagePreview);
        const previewUrl = URL.createObjectURL(file);
        setPendingAboutImageFile(file);
        setPendingAboutImagePreview(previewUrl);
        setEditingAboutData((prev) => ({ ...prev, image_url: previewUrl }));
        setSuccess('Image selected. Click Save Details to store it.');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to upload image';
        setError(msg);
      }
    };
    input.click();
  };

  const PageBuilderManager = () => {
    const removePage = async (id: number) => {
      if (!window.confirm('Delete this page?')) return;
      try {
        await pagesApi.delete(id);
        await loadDashboardData();
      } catch (err) {
        console.error('Failed to delete page', err);
      }
    };

    const togglePublish = async (p: any) => {
      try {
        await pagesApi.update(p.id, { ...p, is_published: !p.is_published });
        await loadDashboardData();
      } catch (err) {
        console.error('Failed to update page status', err);
      }
    };

    return (
      <div className="p-6 md:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Page Builder</h2>
            <p className="text-gray-400 text-sm">Elementor-style drag/drop sections, live preview, and publish control.</p>
          </div>
          <button
            onClick={() => navigate('/dashboard/builder/new')}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-semibold"
          >
            + Create New Page
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pages.map((page: any) => (
            <div key={page.id} className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-semibold text-white truncate">{page.page_title || page.title || 'Untitled Page'}</h3>
                <button
                  onClick={() => togglePublish(page)}
                  className={`text-xs px-2 py-1 rounded border ${page.is_published ? 'text-green-300 border-green-500/40 bg-green-500/10' : 'text-yellow-300 border-yellow-500/40 bg-yellow-500/10'}`}
                >
                  {page.is_published ? 'Published' : 'Draft'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">/{page.page_slug || page.slug || ''}</p>
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={() => navigate(`/dashboard/builder/${page.id}`)}
                  className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-sm"
                >
                  Edit Builder
                </button>
                <a
                  href={`/page/${page.page_slug || page.slug || ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-sm"
                >
                  View
                </a>
                <button
                  onClick={() => removePage(page.id)}
                  className="px-3 py-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {pages.length === 0 && (
          <div className="border border-white/10 rounded-xl p-10 text-center text-gray-400">
            No custom pages yet. Create your first page from Page Builder.
          </div>
        )}
      </div>
    );
  };

  const menuItems = [
    { id: 'hero', label: 'Hero Section', icon: <Home size={20} />, color: 'from-purple-500 to-purple-600' },
    { id: 'services', label: 'Services', icon: <Activity size={20} />, color: 'from-green-500 to-green-600' },
    { id: 'portfolio', label: 'Portfolio', icon: <Box size={20} />, color: 'from-orange-500 to-orange-600' },
    { id: 'works', label: 'Works Section', icon: <BarChart3 size={20} />, color: 'from-amber-500 to-amber-600' },
    { id: 'about', label: 'About', icon: <Users size={20} />, color: 'from-pink-500 to-pink-600' },
    { id: 'contact', label: 'Contact', icon: <Mail size={20} />, color: 'from-red-500 to-red-600' },
    { id: 'pages', label: 'Page Builder', icon: <FileText size={20} />, color: 'from-indigo-500 to-indigo-600' },
    { id: 'header', label: 'Header', icon: <Layers size={20} />, color: 'from-teal-500 to-teal-600' },
    { id: 'footer', label: 'Footer', icon: <Code size={20} />, color: 'from-cyan-500 to-cyan-600' },
    { id: 'settings', label: 'Settings', icon: <Settings size={20} />, color: 'from-gray-500 to-gray-600' },
  ];

  const SimpleOverview = ({ stats }: { stats: any }) => (
  <div className="p-6 space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-gray-400 text-sm">Total Projects</h3>
          <Box className="w-8 h-8 text-orange-400" />
        </div>
        <p className="text-3xl font-bold text-white">{stats?.totalProjects || 0}</p>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-gray-400 text-sm">Total Services</h3>
          <Activity className="w-8 h-8 text-green-400" />
        </div>
        <p className="text-3xl font-bold text-white">{stats?.totalServices || 0}</p>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-gray-400 text-sm">Total Contacts</h3>
          <Mail className="w-8 h-8 text-blue-400" />
        </div>
        <p className="text-3xl font-bold text-white">{stats?.totalContacts || 0}</p>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-gray-400 text-sm">Total Pages</h3>
          <FileText className="w-8 h-8 text-purple-400" />
        </div>
        <p className="text-3xl font-bold text-white">{stats?.totalPages || 0}</p>
      </div>
    </div>
  </div>
);

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <SimpleOverview stats={stats} />;
      case 'hero':
        return <ModernHeroEditor heroSections={heroSections} onUpdate={loadDashboardData} />;
      case 'services':
        return <ModernServicesEditor services={services} onUpdate={loadDashboardData} />;
      case 'portfolio':
        return <ModernPortfolioEditor />;
      case 'works':
        return <WorksSectionEditor />;
      case 'about':
        return (
          <div className="p-6 md:p-8">
            <AboutEditor
              slides={aboutSlides}
              onEdit={handleEditAbout}
              onSave={handleSaveAbout}
              onCreate={handleCreateAbout}
              onToggleActive={handleToggleAboutActive}
              onDelete={handleDeleteAbout}
              showInactive={showInactive}
              editingAboutId={editingAboutId}
              editingAboutData={editingAboutData}
              setEditingAboutData={setEditingAboutData}
              setEditingAboutId={setEditingAboutId}
              modelSource={modelSource}
              setModelSource={setModelSource}
              sectionHeading={aboutHeading.heading}
              sectionDescription={aboutHeading.description}
              sectionCtaHeading={aboutHeading.cta_heading}
              sectionCtaDescription={aboutHeading.cta_description}
              sectionButtonText={aboutHeading.button_text}
              sectionButtonAction={aboutHeading.button_action}
              sectionButtonLink={aboutHeading.button_link}
              onSectionHeadingChange={(value) => setAboutHeading((prev) => ({ ...prev, heading: value }))}
              onSectionDescriptionChange={(value) => setAboutHeading((prev) => ({ ...prev, description: value }))}
              onSectionCtaHeadingChange={(value) => setAboutHeading((prev) => ({ ...prev, cta_heading: value }))}
              onSectionCtaDescriptionChange={(value) => setAboutHeading((prev) => ({ ...prev, cta_description: value }))}
              onSectionButtonTextChange={(value) => setAboutHeading((prev) => ({ ...prev, button_text: value }))}
              onSectionButtonActionChange={(value) => setAboutHeading((prev) => ({ ...prev, button_action: value }))}
              onSectionButtonLinkChange={(value) => setAboutHeading((prev) => ({ ...prev, button_link: value }))}
              onSaveSectionContent={handleSectionHeadingSave}
              headingLimit={255}
              descriptionLimit={1000}
              history={aboutHistoryRef.current}
              onRestoreVersion={(entry) => setAboutHeading({ heading: entry.heading, description: entry.description })}
              onUploadImage={handleUploadAboutImage}
            />
          </div>
        );
      case 'contact':
        return (
          <div className="p-6 md:p-8">
            <ContactManager
              submissions={contactSubmissions}
              onUpdateStatus={handleUpdateContactStatus}
              onDelete={handleDeleteContact}
              onBulkDelete={handleBulkDeleteContact}
            />
          </div>
        );
      case 'pages':
        return <PageBuilderManager />;
      case 'header':
        return (
          <div className="p-6 md:p-8">
            <HeaderSettingsContent />
          </div>
        );
      case 'footer':
        return (
          <div className="p-6 md:p-8">
            <FooterEditor />
          </div>
        );
      case 'theme':
        return <div className="p-8"><h2 className="text-2xl font-bold text-white">Theme Settings</h2></div>;
      case 'preloader':
        return <PreloaderSettings />;
      case 'settings':
        return (
          <div className="p-6 md:p-8">
            <SettingsContent />
          </div>
        );
      default:
        return <SimpleOverview stats={stats} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Modern Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            className="fixed left-0 top-0 h-full w-72 bg-black/50 backdrop-blur-xl border-r border-white/10 z-40 flex flex-col overflow-y-auto"
          >
            <div className="p-6 flex flex-col h-full">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                  <Zap size={24} className="text-white" fill="currentColor" />
                </div>
                <div>
                  <h1 className="text-xl font-black bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                    SOLTECHY
                  </h1>
                  <p className="text-xs text-gray-400 font-mono">CMS DASHBOARD</p>
                </div>
              </div>

              {/* Navigation */}
              <nav className="space-y-2 overflow-y-auto flex-1 pr-2">
                {menuItems.map((item) => (
                  <motion.button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                      activeTab === item.id
                        ? 'bg-gradient-to-r ' + item.color + ' text-white shadow-lg'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={`p-2 rounded-lg ${
                      activeTab === item.id ? 'bg-white/20' : 'bg-white/10'
                    }`}>
                      {item.icon}
                    </div>
                    <span className="font-medium">{item.label}</span>
                    {activeTab === item.id && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute right-4 w-2 h-2 bg-white rounded-full"
                      />
                    )}
                  </motion.button>
                ))}
              </nav>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-0'}`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-black/50 backdrop-blur-xl border-b border-white/10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
              >
                <Menu size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold capitalize">
                  {menuItems.find(item => item.id === activeTab)?.label || 'Dashboard'}
                </h1>
                <p className="text-sm text-gray-400">
                  Manage your {menuItems.find(item => item.id === activeTab)?.label?.toLowerCase() || 'dashboard'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => { authApi.logout(); navigate('/login', { replace: true }); }}
                className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="min-h-screen">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                <p className="text-gray-400">Loading...</p>
              </div>
            </div>
          ) : (
            renderContent()
          )}
        </main>
      </div>

      {/* Notifications Panel */}
      <AnimatePresence>
        {showNotifications && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 right-6 w-80 bg-black/50 backdrop-blur-xl border border-white/10 rounded-xl p-4 z-50"
          >
            <h3 className="text-white font-medium mb-3">Notifications</h3>
            <div className="space-y-2">
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-sm text-white">New contact submission received</p>
                <p className="text-xs text-gray-400">2 minutes ago</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-sm text-white">Project updated successfully</p>
                <p className="text-xs text-gray-400">1 hour ago</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error/Success Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50"
          >
            {error}
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50"
          >
            {success}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernDashboard;
